import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import { Body, Button, ButtonVariant, H2 } from '@meiuca/ui-components'
import { LoginForm } from '@/components/LoginForm'
import { useAuth } from '@/contexts/AuthContext'
import { connectService } from '@/services/connectService'

/**
 * "Sign in with Meiuca" — página aberta como popup por qualquer cliente
 * (training-centers-web, etc.) que queira pedir autorização do AppUser
 * pra vincular contas.
 *
 * Fluxo:
 *   1. Lê `intent` (jti correlation) e `phone` da query string.
 *   2. Se AppUser está logado → confirma vínculo. Caso contrário → reusa
 *      `<LoginForm>`.
 *   3. Após confirmação, `connectService.issueLinkToken(...)` →
 *      `window.opener.postMessage({ type: 'meiuca-link', linkToken }, origin)`
 *      e fecha o popup.
 *
 * `targetOrigin` no postMessage é resolvido contra uma allowlist
 * hardcoded (dev/staging/prod do CT-web).
 */
export default function AuthConnectPage() {
  const [searchParams] = useSearchParams()
  const intent = searchParams.get('intent') ?? ''
  const phone = searchParams.get('phone') ?? ''
  const requestedOrigin = searchParams.get('origin') ?? ''

  const { isAuthenticated, user, isLoading: authLoading } = useAuth()

  type Status = 'verifying' | 'login' | 'issuing' | 'sent' | 'error' | 'missingParams'
  const [status, setStatus] = useState<Status>('verifying')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (authLoading) return
    if (!intent || !phone) {
      setStatus('missingParams')
      return
    }
    setStatus(isAuthenticated ? 'verifying' : 'login')
  }, [authLoading, isAuthenticated, intent, phone])

  function resolveTargetOrigin(): string {
    const allowed = [
      'http://localhost:3002',
      'https://centros-dev.meiucaapp.com.br',
      'https://centros.meiucaapp.com.br',
    ]
    if (allowed.includes(requestedOrigin)) return requestedOrigin
    return allowed[allowed.length - 1]
  }

  async function issueAndSend() {
    setStatus('issuing')
    setErrorMessage(null)
    try {
      const { linkToken } = await connectService.issueLinkToken({ intent, phone })
      const targetOrigin = resolveTargetOrigin()
      window.opener?.postMessage({ type: 'meiuca-link', linkToken }, targetOrigin)
      setStatus('sent')
      setTimeout(() => window.close(), 800)
    } catch (err: any) {
      const code = err?.response?.data?.code
      if (code === 'app_user_connect_phone_mismatch') {
        setErrorMessage(
          'Esta conta usa outro WhatsApp. Saia da conta e entre com a conta cadastrada com o mesmo número.'
        )
      } else if (code === 'app_user_connect_unauthorized' || err?.response?.status === 401) {
        setErrorMessage('Sua sessão expirou. Faça login de novo.')
        setStatus('login')
        return
      } else {
        setErrorMessage('Falha ao vincular a conta. Tente novamente.')
      }
      setStatus('error')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="flex items-center justify-center min-h-screen p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8 border border-white/20"
        >
          {status === 'missingParams' && (
            <div className="py-8 text-center">
              <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <H2 className="text-lg mb-2 text-gray-900">Link inválido</H2>
              <Body className="text-text-secondary">
                Esta página não deve ser aberta diretamente.
              </Body>
            </div>
          )}

          {(status === 'verifying' || status === 'issuing') && (
            <ConfirmView
              userName={user?.name}
              phone={phone}
              isBusy={status === 'issuing'}
              onConfirm={issueAndSend}
            />
          )}

          {status === 'login' && (
            <div>
              <div className="text-center mb-6">
                <H2 className="text-lg text-gray-900 mb-1">Conectar conta Meiuca</H2>
                <Body className="text-text-secondary text-sm">
                  Entre pra vincular o cadastro à sua conta no app.
                </Body>
              </div>
              <LoginForm
                onSuccess={() => {
                  setStatus('verifying')
                }}
              />
            </div>
          )}

          {status === 'sent' && (
            <div className="py-8 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <H2 className="text-lg mb-1 text-gray-900">Conta vinculada!</H2>
              <Body className="text-text-secondary">Fechando…</Body>
            </div>
          )}

          {status === 'error' && (
            <div className="py-6 text-center">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <H2 className="text-lg mb-2 text-gray-900">Falha no vínculo</H2>
              <Body className="text-text-secondary mb-4">{errorMessage}</Body>
              <Button
                variant={ButtonVariant.PRIMARY}
                onClick={() => setStatus(isAuthenticated ? 'verifying' : 'login')}
                className="w-full"
              >
                Tentar novamente
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  )
}

interface ConfirmViewProps {
  userName: string | undefined
  phone: string
  isBusy: boolean
  onConfirm: () => void
}

function ConfirmView({ userName, phone, isBusy, onConfirm }: ConfirmViewProps) {
  const phoneTail = phone.slice(-4)
  return (
    <div className="text-center">
      <H2 className="text-lg text-gray-900 mb-2">Vincular conta</H2>
      <Body className="text-text-secondary mb-6">
        {userName ? <>Você está logado como <strong>{userName}</strong>.</> : 'Você está logado.'}
        {' '}Quer vincular o cadastro (telefone terminando em <strong>{phoneTail}</strong>) a esta conta?
      </Body>
      <Button
        variant={ButtonVariant.PRIMARY}
        onClick={onConfirm}
        disabled={isBusy}
        className="w-full"
      >
        {isBusy ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Vinculando...
          </span>
        ) : (
          'Confirmar vínculo'
        )}
      </Button>
    </div>
  )
}
